/*#
# Similar tasks: the duplicate hint

Two agents file the same thing minutes apart, and a person finds out later
(board #2732: "SVG icon rasterized" was filed just after "improve svg
support"). So filing a task lists the open tasks in its project whose
titles share its words. It never blocks the create: the task is filed, and
the hint says what to do if one of them is the same thing.

```
virta new "SVG icon rasterized"
#2547  SVG icon rasterized  [project:tosijs-platform]
similar open tasks in tosijs-platform (if one is this, close #2547 as duplicate, naming it):
  #2544  improve svg support

virta similar "SVG icon rasterized"     # look before filing; writes nothing
```

Plain word overlap, on titles, case and plurals ignored. Two shared words
make a task similar; so does one, when that word is rare among the
project's open titles (`svg` is). A word a tenth of the titles carry
(`board`) doesn't count. The rarest shared words rank first. `virta list "~svg ~icon"` is the wider search: it reads bodies
too.
*/

/*{ "parent": "Reference", "order": 44 }*/

import type { Task } from './model'
import { PROJECT_KEY, tagValues } from './tags'

/** Words that say nothing about what a task is about. */
const STOP = new Set(
  `a an and are as at be but by can do does for from has have how if in into is it its
   more new no not of on or so than that the then this to too via was when which with
   without add fix make use show should needs need task tasks bug`.split(/\s+/)
)

/** A title's telling words: lowercase, plurals folded, small words dropped. */
export function titleWords(title: string): Set<string> {
  const words = new Set<string>()
  for (const raw of title.toLowerCase().match(/[\p{L}\p{N}]+/gu) ?? []) {
    if (raw.length < 3 || STOP.has(raw)) continue
    const word =
      raw.length > 4 && raw.endsWith('ies')
        ? `${raw.slice(0, -3)}y`
        : raw.length > 3 && raw.endsWith('s') && !raw.endsWith('ss')
        ? raw.slice(0, -1)
        : raw
    if (!STOP.has(word)) words.add(word)
  }
  return words
}

export interface SimilarOptions {
  title: string
  /** look only in this project; every open task when absent */
  project?: string
  /** the most to answer (3) */
  limit?: number
  /** a task to leave out: the one just filed */
  except?: string
}

/**
 * The open tasks whose titles share words with `title`, most alike first.
 * A task counts with two shared words, or one that at most three of the
 * titles looked at carry (2% of them, on a big board). A word a tenth of
 * the titles carry doesn't count at all.
 */
export function similarTasks(
  tasks: Iterable<Task>,
  options: SimilarOptions
): Task[] {
  const wanted = titleWords(options.title)
  if (!wanted.size) return []
  const pool: { task: Task; words: Set<string> }[] = []
  const seen = new Map<string, number>()
  for (const task of tasks) {
    if (task.closedAt || task.id === options.except) continue
    if (
      options.project &&
      !tagValues(task.tags, PROJECT_KEY).includes(options.project)
    )
      continue
    const words = titleWords(task.title)
    pool.push({ task, words })
    for (const w of words) seen.set(w, (seen.get(w) ?? 0) + 1)
  }
  const rareAt = Math.max(3, Math.floor(pool.length * 0.02))
  const scored: { task: Task; score: number }[] = []
  for (const { task, words } of pool) {
    let shared = 0
    let rare = 0
    let score = 0
    for (const w of wanted) {
      if (!words.has(w)) continue
      const n = seen.get(w)!
      // a word a tenth of the titles carry says nothing here
      if (n > rareAt && n > pool.length * 0.1) continue
      shared++
      if (n <= rareAt) rare++
      score += Math.log(1 + pool.length / n)
    }
    if (shared >= 2 || rare >= 1) scored.push({ task, score })
  }
  return scored
    .sort((a, b) => b.score - a.score || b.task.num - a.task.num)
    .slice(0, options.limit ?? 3)
    .map((s) => s.task)
}

/**
 * What filing `task` says about look-alikes: the lines of the hint, or
 * none. The CLI and the MCP server both print these, so they can't
 * disagree.
 */
export function duplicateHint(tasks: Iterable<Task>, task: Task): string[] {
  const project = tagValues(task.tags, PROJECT_KEY)[0]
  const alike = similarTasks(tasks, {
    title: task.title,
    project,
    except: task.id,
  })
  if (!alike.length) return []
  return [
    `similar open tasks${
      project ? ` in ${project}` : ''
    } (if one is this, close #${task.num} as duplicate, naming it):`,
    ...alike.map((t) => `  #${t.num}  ${t.title}`),
  ]
}
